import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../i18n';
import {
    classifyVerifyResult,
    isPackageNr,
    parseReturnQuery,
    successBodyKey,
    verifyPollAttempts,
    VERIFY_POLL_INTERVAL_MS,
} from '../Payment/paymentSubject';
import { EVERYPAY_SUBJECTS } from '../Payment/paymentLinkSwitch';

// REMOVE in cleanup phase (spec §4.11): ws1 base is only used by the Montonio branch.
const API_BASE = 'https://api.wiseclean.ee/api:56Z3lZ-0';
// ws5 `client` apigroup: hosts the public package status read and pay/verify.
const PACKAGE_API_BASE = 'https://api.wiseclean.ee/api:nnuLSRT_';
const VERIFY_ENDPOINT = `${PACKAGE_API_BASE}/pay/verify`;
// pay/verify may poke the LHV callback twice (up to ~30 s server-side). A request
// that hangs past this is abandoned and counted as `pending`, so the poll loop
// always advances and the spinner never sticks.
const VERIFY_TIMEOUT_MS = 40000;
const SUPPORT_EMAIL = 'info@wiseclean.eu';
const HOMEPAGE = 'https://wiseclean.eu';
const REDIRECT_DELAY_MS = 6000;
// Package return (?nr=): pauses between reads of the package row before showing
// "payment is being processed". The first read already re-drives the callback; the
// later ones mostly wait for EveryPay's own callback (~6 s settle) to finish, so the
// pauses grow: 6 reads over about 19 s plus read time. Each read re-drives at most a
// few callbacks server-side (packages_nr_GET), so the window is kept short.
const PACKAGE_POLL_DELAYS_MS = [2000, 3000, 4000, 5000, 5000];
// One read of packages/{nr} can take a while when it re-drives callbacks; past this
// it is abandoned and counted as "not readable" so the poll always advances.
const PACKAGE_READ_TIMEOUT_MS = 40000;

// Cadence values come from Airtable and are stored verbatim on the package row.
// This map only supplies a translated caption for the three that exist today;
// anything else renders its raw value rather than disappearing from the summary.
const CADENCE_LABEL_KEYS = {
    'Weekly': 'frequencyWeekly',
    'Bi-weekly': 'frequencyBiweekly',
    'Tri-weekly': 'frequencyTriweekly',
};

function classifyPaymentStatus(raw) {
    if (raw == null) return 'failed';
    const s = String(raw).trim().toUpperCase();
    if (s === 'PAID' || s === 'AUTHORIZED' || s === 'COMPLETED' || s === 'FINALIZED') {
        return 'success';
    }
    if (s === 'PENDING' || s === 'CREATED' || s === 'PROCESSING') {
        return 'pending';
    }
    return 'failed';
}

function formatMoney(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '';
    return `€${n.toFixed(2)}`;
}

function SuccessPayment() {
    const { lang } = useParams();
    const [searchParams] = useSearchParams();
    // EveryPay return (spec §4.2): pay/start builds customer_url with
    // ?subject=<s>&id=<recid>[&m=bank]. This is the primary branch. Memoised so the
    // polling effect below is keyed on the URL, not on a fresh object per render.
    const verifyReturn = useMemo(() => parseReturnQuery(searchParams), [searchParams]);
    const isVerifyReturn = Boolean(verifyReturn);
    // REMOVE in cleanup phase (spec §4.11): Montonio appends ?order-token=<JWT> on its
    // return redirect. Some banks/integrations historically also used "payment_token".
    // Kept only for the tail window after the cutover; ?subject=&id= always wins.
    const orderToken = isVerifyReturn
        ? null
        : (searchParams.get('order-token') || searchParams.get('payment_token'));
    // The package checkout sends the buyer back with ?nr=<packageNr> and no payment
    // token at all (wiseclean-client/vendor/xano/api/client/packages_checkout_POST.xs
    // builds customer_url that way), so that return is resolved from the package row.
    // Only a real Package.Nr is accepted: the value is echoed into the error copy,
    // so free text from a crafted link must never reach the screen.
    const rawPackageNr = searchParams.get('nr');
    const packageNr = isPackageNr(rawPackageNr) ? rawPackageNr.trim() : null;
    const isPackageReturn = !isVerifyReturn && !orderToken && Boolean(packageNr);
    const { t, i18n } = useTranslation();

    const activeLang = ['en', 'et'].includes(lang) ? lang : 'en';

    const [step, setStep] = useState('loading');
    const [pkg, setPkg] = useState(null);
    const [secondsToHome, setSecondsToHome] = useState(Math.round(REDIRECT_DELAY_MS / 1000));

    useEffect(() => {
        i18n.changeLanguage(activeLang);
    }, [activeLang, i18n]);

    // REMOVE in cleanup phase (spec §4.11): Montonio ?order-token= verification.
    useEffect(() => {
        if (isPackageReturn || isVerifyReturn) {
            // Handled by the package / verify effects below.
            return undefined;
        }
        if (!orderToken) {
            setStep('invalid');
            return undefined;
        }
        let cancelled = false;
        fetch(`${API_BASE}/old_order/check_payment?payment_token=${encodeURIComponent(orderToken)}`)
            .then(async (r) => {
                // The endpoint returns a JSON value (string or object) on success.
                // On error it returns a JSON object with code/message but still status>=400.
                let body = null;
                try { body = await r.json(); } catch (e) { body = null; }
                return { ok: r.ok, body };
            })
            .then(({ ok, body }) => {
                if (cancelled) return;
                if (!ok) {
                    setStep('failed');
                    return;
                }
                // Body shape: either a bare string ("PAID"), or an object with paymentStatus.
                const raw = (body && typeof body === 'object')
                    ? (body.paymentStatus || body.status || null)
                    : body;
                setStep(classifyPaymentStatus(raw));
            })
            .catch(() => {
                if (!cancelled) setStep('failed');
            });
        return () => {
            cancelled = true;
        };
    }, [orderToken, isPackageReturn, isVerifyReturn]);

    // EveryPay return: poll pay/verify {subject, id} until a terminal state. ws5
    // self-calls the lhv-callback for a pending attempt on every read (spec §4.3), so
    // a slow bank redirect usually flips to paid inside the window (6 x 2 s; 8 x when
    // the customer paid through a bank link, ?m=bank). An unreachable verify or an
    // exhausted window lands on `pending`, never on `failed`: the sweep and the email
    // still tell the truth, and "failed" would tell the customer to pay again.
    useEffect(() => {
        if (!verifyReturn) return undefined;
        let cancelled = false;
        let timer = null;
        const maxAttempts = verifyPollAttempts(verifyReturn.bank);
        setStep('loading');
        let controller = null;
        const poll = async (attempt) => {
            let result = 'pending';
            controller = new AbortController();
            const abortTimer = setTimeout(() => controller.abort(), VERIFY_TIMEOUT_MS);
            try {
                const r = await fetch(VERIFY_ENDPOINT, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ subject: verifyReturn.subject, id: verifyReturn.id }),
                    signal: controller.signal,
                });
                let body = null;
                try { body = await r.json(); } catch (e) { body = null; }
                if (r.ok && body && typeof body === 'object') result = classifyVerifyResult(body);
            } catch (e) {
                result = 'pending';
            } finally {
                clearTimeout(abortTimer);
            }
            if (cancelled) return;
            if (result !== 'pending' || attempt >= maxAttempts) {
                setStep(result);
                return;
            }
            timer = setTimeout(() => poll(attempt + 1), VERIFY_POLL_INTERVAL_MS);
        };
        poll(1);
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
            if (controller) controller.abort();
        };
    }, [verifyReturn]);

    // Package return: poll the package row by Nr until it reads paid. The endpoint is
    // public, takes only {nr} and re-drives the payment callback when the row is still
    // unpaid. ONE read is not enough: EveryPay redirects the browser at the same moment
    // it sends its own callback, which holds the per-payment claim for ~6 s while it
    // settles. The page's re-drive loses that claim, re-reads the row before the PATCH
    // lands and answers is_paid:false. Package 177 (2026-10-13) was paid and confirmed
    // ~3 s after its buyer had already been shown "we have not received the payment".
    // So keep the spinner and read again (growing pauses, PACKAGE_POLL_DELAYS_MS); only
    // an exhausted window shows "pending".
    useEffect(() => {
        if (!isPackageReturn) return undefined;
        let cancelled = false;
        let timer = null;
        let controller = null;
        // The newest readable row: a transient error on the LAST read must not turn a
        // known unpaid package into the error screen.
        let lastRow = null;
        setStep('loading');
        setPkg(null);
        const read = async (attempt) => {
            let result = null;
            let notFound = false;
            controller = new AbortController();
            const abortTimer = setTimeout(() => controller.abort(), PACKAGE_READ_TIMEOUT_MS);
            try {
                const r = await fetch(`${PACKAGE_API_BASE}/packages/${encodeURIComponent(packageNr)}`, {
                    signal: controller.signal,
                });
                let body = null;
                try { body = await r.json(); } catch (e) { body = null; }
                result = r.ok && body && typeof body === 'object' ? body : null;
                // 400-404 cannot get better by waiting (unknown Nr, bad input).
                notFound = r.status >= 400 && r.status <= 404;
            } catch (e) {
                result = null;
            } finally {
                clearTimeout(abortTimer);
            }
            if (cancelled) return;
            if (result) lastRow = result;
            if (notFound && !lastRow) {
                setStep('packageError');
                return;
            }
            // Strict equality on purpose: only a real boolean true claims the payment
            // landed. Anything else (missing, string, 0) reads as "still processing",
            // which is the safe direction to be wrong in.
            if (result && result.is_paid === true) {
                setPkg(result);
                setStep('packagePaid');
                return;
            }
            if (attempt <= PACKAGE_POLL_DELAYS_MS.length) {
                if (lastRow) setPkg(lastRow);
                timer = setTimeout(() => read(attempt + 1), PACKAGE_POLL_DELAYS_MS[attempt - 1]);
                return;
            }
            // Window exhausted: a known unpaid row is "still processing" (the callback
            // and the confirmation email still tell the truth); no readable row at all
            // is the error screen.
            if (lastRow) {
                setPkg(lastRow);
                setStep('packagePending');
            } else {
                setStep('packageError');
            }
        };
        read(1);
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
            if (controller) controller.abort();
        };
    }, [isPackageReturn, packageNr]);

    // Auto-redirect to homepage on success after REDIRECT_DELAY_MS, with a visible countdown.
    useEffect(() => {
        if (step !== 'success') return undefined;
        const startedAt = Date.now();
        setSecondsToHome(Math.round(REDIRECT_DELAY_MS / 1000));
        const tick = setInterval(() => {
            const remaining = Math.max(0, REDIRECT_DELAY_MS - (Date.now() - startedAt));
            setSecondsToHome(Math.ceil(remaining / 1000));
        }, 250);
        const final = setTimeout(() => {
            window.location.href = `${HOMEPAGE}/${activeLang}`;
        }, REDIRECT_DELAY_MS);
        return () => {
            clearInterval(tick);
            clearTimeout(final);
        };
    }, [step, activeLang]);

    const goHome = () => {
        window.location.href = `${HOMEPAGE}/${activeLang}`;
    };

    // A failed EveryPay payment goes back to its own payment link, for the subjects
    // the EveryPay page serves (EVERYPAY_SUBJECTS: package, order, completed, tip;
    // the page accepts their rec ids). Since the cutover a failed tip retries on
    // /external/payment/...?subject=tip like the others. Every other failure keeps
    // the old behaviour: the homepage.
    const retryPayment = () => {
        if (verifyReturn && EVERYPAY_SUBJECTS.includes(verifyReturn.subject)) {
            window.location.href = `/external/payment/${activeLang}/${encodeURIComponent(verifyReturn.id)}?subject=${verifyReturn.subject}`;
            return;
        }
        goHome();
    };

    // The generic receipt line ("Thank you! Your receipt is on its way ...") belongs
    // to the legacy order flows. An EveryPay return already shows its own
    // subject-specific line, so the second thank-you is dropped there.
    const showLegacyReceiptLine = !verifyReturn || verifyReturn.subject === 'order';

    if (step === 'loading') {
        return (
            <div style={styles.page}>
                <header style={styles.header}>
                    <img src="/wiseclean-logo.png" alt="WiseClean" style={styles.logo} />
                </header>
                <main style={styles.main}>
                    <div style={styles.loadingText}>{t('successPaymentChecking')}</div>
                </main>
            </div>
        );
    }

    if (step === 'packagePaid' || step === 'packagePending' || step === 'packageError') {
        const cadenceKey = CADENCE_LABEL_KEYS[pkg?.regular];
        const cadenceLabel = cadenceKey ? t(cadenceKey) : (pkg?.regular || '');
        let firstCleaning = '';
        if (pkg?.date) {
            firstCleaning = pkg.time
                ? t('successPaymentPackageDateTime', { date: pkg.date, time: pkg.time })
                : pkg.date;
        }
        const details = step === 'packageError' ? [] : [
            [t('successPaymentPackageNumber'), pkg?.nr ? `#${pkg.nr}` : ''],
            [t('successPaymentPackageType'), pkg?.package_type || ''],
            [t('frequencyLabel'), cadenceLabel],
            [t('successPaymentPackageFirstCleaning'), firstCleaning],
            [
                t('successPaymentPackageCleanings'),
                pkg?.cleanings_included > 0 ? String(pkg.cleanings_included) : '',
            ],
            step === 'packagePaid'
                ? [t('successPaymentPackagePaid'), formatMoney(pkg?.paid)]
                : [t('totalOnlyLabel'), formatMoney(pkg?.summ)],
        ].filter(([, value]) => value);

        return (
            <div style={styles.page}>
                <header style={styles.header}>
                    <img src="/wiseclean-logo.png" alt="WiseClean" style={styles.logo} />
                </header>
                <main style={styles.main}>
                    <div style={styles.card}>
                        {step === 'packagePaid' && (
                            <>
                                <div style={styles.iconSuccess} aria-hidden="true">✓</div>
                                <h1 style={styles.title}>{t('successPaymentPackageTitle')}</h1>
                                <p style={styles.body}>{t('successPaymentPackageBody')}</p>
                            </>
                        )}
                        {step === 'packagePending' && (
                            <>
                                <div style={styles.iconPending} aria-hidden="true">…</div>
                                <h1 style={styles.title}>{t('successPaymentPendingTitle')}</h1>
                                <p style={styles.body}>{t('successPaymentPackagePendingBody')}</p>
                            </>
                        )}
                        {step === 'packageError' && (
                            <>
                                <h1 style={styles.errorTitle}>{t('loadFailedTitle')}</h1>
                                <p style={styles.body}>
                                    {t('successPaymentPackageErrorBody', { nr: packageNr })}
                                </p>
                            </>
                        )}
                        {details.length > 0 && (
                            <div style={styles.detailsBlock}>
                                {details.map(([label, value]) => (
                                    <p key={label} style={styles.detailLine}>
                                        <strong style={styles.detailLabel}>{label}</strong>
                                        <span style={styles.detailValue}>{value}</span>
                                    </p>
                                ))}
                            </div>
                        )}
                        <button type="button" style={styles.primaryButton} onClick={goHome}>
                            {t('successPaymentGoHome')}
                        </button>
                        {step !== 'packagePaid' && (
                            <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.supportLink}>
                                {t('contactSupport')} · {SUPPORT_EMAIL}
                            </a>
                        )}
                    </div>
                </main>
            </div>
        );
    }

    if (step === 'invalid') {
        return (
            <div style={styles.page}>
                <header style={styles.header}>
                    <img src="/wiseclean-logo.png" alt="WiseClean" style={styles.logo} />
                </header>
                <main style={styles.errorMain}>
                    <div style={styles.card}>
                        <h1 style={styles.errorTitle}>{t('loadFailedTitle')}</h1>
                        <p style={styles.body}>{t('successPaymentInvalidLink')}</p>
                        <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.supportLink}>
                            {t('contactSupport')} · {SUPPORT_EMAIL}
                        </a>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <header style={styles.header}>
                <img src="/wiseclean-logo.png" alt="WiseClean" style={styles.logo} />
            </header>
            <main style={styles.main}>
                <div style={styles.card}>
                    {step === 'success' && (
                        <>
                            <div style={styles.iconSuccess} aria-hidden="true">✓</div>
                            <h1 style={styles.title}>{t('successPaymentSuccessTitle')}</h1>
                            <p style={styles.body}>{t(successBodyKey(verifyReturn ? verifyReturn.subject : 'order'))}</p>
                            {showLegacyReceiptLine && (
                                <p style={styles.bodyMuted}>
                                    {t('successPaymentSuccessBody')}
                                </p>
                            )}
                            <button type="button" style={styles.primaryButton} onClick={goHome}>
                                {t('successPaymentGoHome')}
                                {secondsToHome > 0 ? ` (${secondsToHome})` : ''}
                            </button>
                        </>
                    )}
                    {step === 'pending' && (
                        <>
                            <div style={styles.iconPending} aria-hidden="true">…</div>
                            <h1 style={styles.title}>{t('successPaymentPendingTitle')}</h1>
                            <p style={styles.body}>{t('successPaymentPendingBody')}</p>
                            <button type="button" style={styles.secondaryButton} onClick={goHome}>
                                {t('successPaymentGoHome')}
                            </button>
                        </>
                    )}
                    {step === 'checking' && (
                        <>
                            <div style={styles.iconPending} aria-hidden="true">…</div>
                            <h1 style={styles.title}>{t('successCheckingTitle')}</h1>
                            <p style={styles.body}>{t('successCheckingBody')}</p>
                            <button type="button" style={styles.secondaryButton} onClick={goHome}>
                                {t('successPaymentGoHome')}
                            </button>
                            <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.supportLink}>
                                {t('contactSupport')} · {SUPPORT_EMAIL}
                            </a>
                        </>
                    )}
                    {step === 'failed' && (
                        <>
                            <div style={styles.iconFailed} aria-hidden="true">!</div>
                            <h1 style={styles.title}>{t('successPaymentFailedTitle')}</h1>
                            <p style={styles.body}>{t('successPaymentFailedBody')}</p>
                            <button type="button" style={styles.primaryButton} onClick={retryPayment}>
                                {t('successPaymentTryAgain')}
                            </button>
                            <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.supportLink}>
                                {t('contactSupport')} · {SUPPORT_EMAIL}
                            </a>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}

const styles = {
    page: {
        minHeight: '100vh',
        background: 'var(--wc-bg-secondary)',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'center',
    },
    logo: {
        height: '36px',
        width: 'auto',
        objectFit: 'contain',
    },
    main: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
    },
    errorMain: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
    },
    loadingText: {
        color: 'var(--wc-text-secondary)',
        fontSize: '15px',
        padding: '40px 20px',
    },
    card: {
        width: '100%',
        maxWidth: '440px',
        background: 'var(--wc-bg-primary)',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-card)',
        boxShadow: 'var(--wc-shadow-card)',
        padding: '32px 24px',
        boxSizing: 'border-box',
        textAlign: 'center',
    },
    iconSuccess: {
        width: '56px',
        height: '56px',
        margin: '0 auto 16px',
        borderRadius: '50%',
        background: 'var(--wc-success)',
        color: 'var(--wc-on-brand)',
        fontSize: '32px',
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
    },
    iconPending: {
        width: '56px',
        height: '56px',
        margin: '0 auto 16px',
        borderRadius: '50%',
        background: 'var(--wc-brand)',
        color: 'var(--wc-on-brand)',
        fontSize: '32px',
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 0.5,
    },
    iconFailed: {
        width: '56px',
        height: '56px',
        margin: '0 auto 16px',
        borderRadius: '50%',
        background: 'var(--wc-error)',
        color: 'var(--wc-on-brand)',
        fontSize: '30px',
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
    },
    title: {
        margin: '0 0 12px',
        fontSize: '22px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
    },
    body: {
        margin: '0 0 12px',
        fontSize: '15px',
        color: 'var(--wc-text-primary)',
        lineHeight: 1.5,
    },
    bodyMuted: {
        margin: '0 0 20px',
        fontSize: '14px',
        color: 'var(--wc-text-secondary)',
        lineHeight: 1.5,
    },
    detailsBlock: {
        background: 'var(--wc-bg-secondary)',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-input)',
        padding: '14px',
        margin: '4px 0 20px',
        textAlign: 'left',
    },
    detailLine: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '12px',
        margin: '6px 0',
        fontSize: '13px',
        lineHeight: 1.45,
    },
    detailLabel: {
        color: 'var(--wc-text-secondary)',
        fontWeight: 600,
    },
    detailValue: {
        color: 'var(--wc-text-primary)',
        textAlign: 'right',
    },
    primaryButton: {
        display: 'block',
        width: '100%',
        background: 'var(--wc-brand)',
        color: 'var(--wc-on-brand)',
        border: 'none',
        borderRadius: 'var(--wc-radius-btn)',
        padding: '14px 16px',
        fontSize: '16px',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
        boxShadow: 'var(--wc-shadow-cta)',
        marginBottom: '12px',
    },
    secondaryButton: {
        display: 'block',
        width: '100%',
        background: 'transparent',
        color: 'var(--wc-brand-dark)',
        border: '1px solid var(--wc-brand)',
        borderRadius: 'var(--wc-radius-btn)',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
    },
    supportLink: {
        display: 'inline-block',
        marginTop: '8px',
        color: 'var(--wc-brand-dark)',
        fontWeight: 600,
        textDecoration: 'none',
        fontSize: '14px',
    },
    errorTitle: {
        margin: '0 0 12px',
        fontSize: '20px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
    },
};

export default SuccessPayment;
